import React, { useState, useEffect } from 'react';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { DatePickerComponent, TimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { type CrudFormProps } from '../../components/CrudPage';
import { type DeliveryDto, type MaterialDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { useTranslation } from 'react-i18next';
import { MaterialControllerService } from '../../api';

type DeliveryWithId = DeliveryDto & { id: string };

const DeliveryFormDialog: React.FC<CrudFormProps<DeliveryWithId>> = ({
  value,
  onClose,
  onSave
}) => {
  const { t, i18n } = useTranslation();
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [filteredMaterials, setFilteredMaterials] = useState<MaterialDto[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialDto | null>(null);
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    { text: t('delivery.status.PENDING'), value: 'PENDING' },
    { text: t('delivery.status.IN_TRANSIT'), value: 'IN_TRANSIT' },
    { text: t('delivery.status.RECEIVED'), value: 'RECEIVED' },
    { text: t('delivery.status.CANCELLED'), value: 'CANCELLED' },
    { text: t('delivery.status.DELAYED'), value: 'DELAYED' }
  ];

  // Helper function to format unit type using existing translations
  const formatUnitType = (unitType: string) => {
    switch (unitType) {
      case 'PIECE': return t('unitType.piece');
      case 'MASS': return t('unitType.mass');
      case 'VOLUME': return t('unitType.volume');
      case 'LENGTH': return t('unitType.length');
      case 'AREA': return t('unitType.area');
      default: return unitType;
    }
  };

  useEffect(() => {
    loadMaterials();
  }, []);

  useEffect(() => {
    // Filter materials to only show BUY type materials
    const buyMaterials = materials.filter(material => material.productionType === 'BUY');
    setFilteredMaterials(buyMaterials);
  }, [materials]);

  useEffect(() => {
    // Set selected material when value changes (for edit mode)
    if (value?.materialId) {
      const material = materials.find(m => m.uuid === value.materialId);
      setSelectedMaterial(material || null);
    }
  }, [value?.materialId, materials]);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const materialsData = await MaterialControllerService.findAll();
      setMaterials(materialsData);
    } catch (error) {
      console.error('Error loading materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialChange = (materialId: string) => {
    const material = materials.find(m => m.uuid === materialId);
    setSelectedMaterial(material || null);
  };

  return (
    <FormDialog
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey={value?.id ? 'delivery.form.editTitle' : 'delivery.form.createTitle'}
    >
      {({ register, formState: { errors }, setValue, watch }) => (
        <>
          {/* Material Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('delivery.form.material')} *
            </label>
            <DropDownListComponent
              dataSource={filteredMaterials}
              fields={{ text: 'name', value: 'uuid' }}
              placeholder={t('delivery.form.materialPlaceholder')}
              floatLabelType="Never"
              cssClass={errors.materialId ? 'e-error' : ''}
              enabled={!loading}
              value={watch('materialId')}
                          onChange={(e: any) => {
                setValue('materialId', e.value);
                handleMaterialChange(e.value);
              }}
            />
            {errors.materialId && (
              <div className="text-red-500 text-sm mt-1">{errors.materialId.message}</div>
            )}
          </div>

          {/* Base Unit (Read-only) */}
          <div className="mb-4">
            <TextBoxComponent
              value={selectedMaterial?.baseUnitType ? formatUnitType(selectedMaterial.baseUnitType) : ''}
              placeholder={t('delivery.form.baseUnitPlaceholder')}
              floatLabelType="Never"
              readonly={true}
              enabled={false}
            />
          </div>

          {/* Quantity */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('delivery.form.quantity')} *
            </label>
            <TextBoxComponent
              {...register('quantity', { 
                required: t('validation.required'),
                min: { value: 0.0001, message: t('validation.min', { min: 0.0001 }) }
              })}
              placeholder={t('delivery.form.quantityPlaceholder')}
              floatLabelType="Never"
              cssClass={errors.quantity ? 'e-error' : ''}
              type="number"
            />
            {errors.quantity && (
              <div className="text-red-500 text-sm mt-1">{errors.quantity.message}</div>
            )}
          </div>

          {/* Expected Date */}
          <div className="mb-4">
            <DatePickerComponent
              {...register('expectedDate')}
              placeholder={t('delivery.form.expectedDatePlaceholder')}
              floatLabelType="Never"
              cssClass={errors.expectedDate ? 'e-error' : ''}
              format={i18n.language === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy'}
            />
            {errors.expectedDate && (
              <div className="text-red-500 text-sm mt-1">{errors.expectedDate.message}</div>
            )}
          </div>

          {/* Expected Time (Optional) */}
          <div className="mb-4">
            <TimePickerComponent
              placeholder={t('delivery.form.expectedTimePlaceholder')}
              floatLabelType="Never"
              format="HH:mm"
              showClearButton={true}
            />
          </div>

          {/* Actual Date */}
          <div className="mb-4">
            <DatePickerComponent
              {...register('actualDate')}
              placeholder={t('delivery.form.actualDatePlaceholder')}
              floatLabelType="Never"
              cssClass={errors.actualDate ? 'e-error' : ''}
              format={i18n.language === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy'}
            />
            {errors.actualDate && (
              <div className="text-red-500 text-sm mt-1">{errors.actualDate.message}</div>
            )}
          </div>

          {/* Actual Time (Optional) */}
          <div className="mb-4">
            <TimePickerComponent
              placeholder={t('delivery.form.actualTimePlaceholder')}
              floatLabelType="Never"
              format="HH:mm"
              showClearButton={true}
            />
          </div>

          {/* Status */}
          <div className="mb-4">
            <DropDownListComponent
              dataSource={statusOptions}
              fields={{ text: 'text', value: 'value' }}
              placeholder={t('delivery.form.statusPlaceholder')}
              floatLabelType="Never"
              cssClass={errors.status ? 'e-error' : ''}
              value={watch('status')}
                          onChange={(e: any) => {
                setValue('status', e.value);
              }}
            />
            {errors.status && (
              <div className="text-red-500 text-sm mt-1">{errors.status.message}</div>
            )}
          </div>

          {/* Supplier */}
          <div className="mb-4">
            <TextBoxComponent
              {...register('supplier')}
              placeholder={t('delivery.form.supplierPlaceholder')}
              floatLabelType="Never"
              cssClass={errors.supplier ? 'e-error' : ''}
            />
            {errors.supplier && (
              <div className="text-red-500 text-sm mt-1">{errors.supplier.message}</div>
            )}
          </div>

          {/* Notes */}
          <div className="mb-4">
            <TextBoxComponent
              {...register('notes')}
              placeholder={t('delivery.form.notesPlaceholder')}
              floatLabelType="Never"
              cssClass={errors.notes ? 'e-error' : ''}
              multiline={true}
            />
            {errors.notes && (
              <div className="text-red-500 text-sm mt-1">{errors.notes.message}</div>
            )}
          </div>
        </>
      )}
    </FormDialog>
  );
};

export default DeliveryFormDialog; 